import { useEffect, useState } from 'react';
import { Alert, Button, FormHelperText, Grid, InputLabel, MenuItem, OutlinedInput, Select } from '@mui/material';
import QuizIcon from '@mui/icons-material/Quiz';
import { FormDrawer, FormDrawerGroup } from '../../../../components/form-drawer';
import { RichTextEditor } from '../../components/rich-text-editor';
import { isRichTextEmpty } from '../../components/rich-text-utils';
import { IQuestionAnswerAddEditRequest } from '../../../../model/apm/question-answer/request/question-answer-add-edit-request';
import { IQuestionAnswerResponse } from '../../../../model/apm/question-answer/response/question-answer-response';
import { IQuestionAnswerCategoryResponse } from '../../../../model/apm/question-answer/response/question-answer-category-response';

interface QuestionAnswerDrawerProps {
    open: boolean;
    initial?: IQuestionAnswerResponse;
    categories: IQuestionAnswerCategoryResponse[];
    isSaving: boolean;
    onClose: () => void;
    onSave: (value: IQuestionAnswerAddEditRequest) => void;
    onManageCategories: () => void;
}

export function QuestionAnswerDrawer({ open, initial, categories, isSaving, onClose, onSave, onManageCategories }: QuestionAnswerDrawerProps) {
    const [form, setForm] = useState<IQuestionAnswerAddEditRequest>({});
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const isEdit = !!initial;
    const categoryError = showValidationErrors && !form.categoryId;
    const questionError = showValidationErrors && !form.question?.trim();
    const answerError = showValidationErrors && isRichTextEmpty(form.answer);

    useEffect(() => {
        if (!open) return;
        setForm(initial ? { categoryId: initial.categoryId, question: initial.question, answer: initial.answer } : {});
        setShowValidationErrors(false);
    }, [open, initial]);

    function update(patch: Partial<IQuestionAnswerAddEditRequest>) {
        setForm((current) => ({ ...current, ...patch }));
    }

    function handleSave() {
        if (!form.categoryId || !form.question?.trim() || isRichTextEmpty(form.answer)) {
            setShowValidationErrors(true);
            return;
        }
        onSave({ ...form, question: form.question.trim() });
    }

    // Dados legados podem apontar para categoria de outra entidade; mantém a opção para a pergunta não perder o vínculo
    const categoryOptions = initial?.categoryId && !categories.some((c) => c.id === initial.categoryId)
        ? [...categories, { id: initial.categoryId, description: initial.category || `Categoria ${initial.categoryId}`, questionCount: 0 }]
        : categories;

    return (
        <FormDrawer
            open={open}
            icon={<QuizIcon />}
            title={isEdit ? 'Editar pergunta' : 'Nova pergunta'}
            subtitle={isEdit ? initial?.category : 'Pergunta frequente exibida no portal'}
            saveLabel={isEdit ? 'Salvar alterações' : 'Adicionar pergunta'}
            isSaving={isSaving}
            onClose={onClose}
            onSave={handleSave}
        >
            <FormDrawerGroup title="Identificação" description="A categoria agrupa as perguntas na página de perguntas frequentes.">
                {categoryOptions.length === 0 ? (
                    <Alert severity="warning" action={<Button color="inherit" size="small" onClick={onManageCategories}>Cadastrar</Button>}>
                        Cadastre uma categoria antes de adicionar perguntas.
                    </Alert>
                ) : (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <InputLabel required>Categoria</InputLabel>
                            <Select
                                fullWidth
                                value={form.categoryId ?? ''}
                                onChange={(e) => update({ categoryId: e.target.value === '' ? undefined : Number(e.target.value) })}
                                error={categoryError}
                            >
                                {categoryOptions.map((c) => <MenuItem key={c.id} value={c.id}>{c.description}</MenuItem>)}
                            </Select>
                            {categoryError && <FormHelperText error>Categoria é obrigatória</FormHelperText>}
                        </Grid>
                    </Grid>
                )}
            </FormDrawerGroup>

            <FormDrawerGroup title="Conteúdo">
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <InputLabel required>Pergunta</InputLabel>
                        <OutlinedInput
                            fullWidth
                            multiline
                            minRows={2}
                            autoFocus
                            autoComplete="off"
                            value={form.question || ''}
                            onChange={(e) => update({ question: e.target.value })}
                            error={questionError}
                        />
                        {questionError && <FormHelperText error>Pergunta é obrigatória</FormHelperText>}
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel required>Resposta</InputLabel>
                        <RichTextEditor value={form.answer} onChange={(answer) => update({ answer })} error={answerError} minHeight={200} />
                        {answerError && <FormHelperText error>Resposta é obrigatória</FormHelperText>}
                    </Grid>
                </Grid>
            </FormDrawerGroup>
        </FormDrawer>
    );
}
